import { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import { MaintenanceProviderConnected } from '@/contexts/MaintenanceProviderConnected';
import { useUser } from '@/contexts/UserContext';
import { ErrorPage } from '@/pages/Error/ErrorPage';
import { LoadingPage } from '@/pages/Loading/Loading';

function AuthenticatedRoute(): ReactNode {
  const { currentUser } = useUser();
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();

  if (!isAuthenticated && !currentUser) {
    return (
      <Navigate to="login" state={{ from: location }} />
    );
  }

  if (currentUser === undefined) {
    return <LoadingPage />;
  }

  if (currentUser === null) {
    return (
      <ErrorPage
        message="Your user either doesn't exist in our database, or you are not logged in."
        returnTo="login"
      />
    );
  }

  return (
    <NotificationsProvider>
      <MaintenanceProviderConnected>
        <Outlet />
      </MaintenanceProviderConnected>
    </NotificationsProvider>
  );
}

function NoAuthRoute(): ReactNode {
  const { currentUser } = useUser();

  if (currentUser === undefined) {
    return <LoadingPage />;
  }

  return (
    <NotificationsProvider>
      <MaintenanceProviderConnected>
        <Outlet />
      </MaintenanceProviderConnected>
    </NotificationsProvider>
  );
}

export function PrivateRoute(): ReactNode {
  if (import.meta.env.VITE_DISABLE_AUTH === 'true') {
    return <NoAuthRoute />;
  }

  return <AuthenticatedRoute />;
}
